import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  matrixQueries,
  updateMatrixColumn,
  type MatrixUi,
  type UpdateMatrixColumnInput,
} from "@/domains/matrix";

type UpdateMatrixColumnParams = {
  columnKey: string;
  input: UpdateMatrixColumnInput;
};

type UpdateMatrixColumnContext = {
  previous?: MatrixUi;
};

// Optimistic, so a resized column keeps its width until the refetch lands.
export function useUpdateMatrixColumn(collectionId: string) {
  const queryClient = useQueryClient();
  const queryKey = matrixQueries.detail(collectionId).queryKey;

  return useMutation<
    { key: string },
    unknown,
    UpdateMatrixColumnParams,
    UpdateMatrixColumnContext
  >({
    mutationFn: ({ columnKey, input }) =>
      updateMatrixColumn(collectionId, columnKey, input),
    onMutate: async ({ columnKey, input }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<MatrixUi>(queryKey);

      if (previous) {
        queryClient.setQueryData<MatrixUi>(queryKey, {
          ...previous,
          columns: previous.columns.map((column) =>
            column.key === columnKey
              ? {
                  ...column,
                  ...(input.label === undefined ? {} : { label: input.label }),
                  ...(input.type === undefined ? {} : { type: input.type }),
                  ...(input.width === undefined ? {} : { width: input.width }),
                  ...(input.order === undefined ? {} : { order: input.order }),
                  ...(input.displayDescription === undefined
                    ? {}
                    : { displayDescription: input.displayDescription }),
                }
              : column,
          ),
        });
      }

      return { previous };
    },
    onError: (_error, _params, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: (_data, _error, { input }) => {
      void queryClient.invalidateQueries(matrixQueries.detail(collectionId));
      // A resize changes no label and fires on every drag, so it stays local.
      if (input.width === undefined) {
        void queryClient.invalidateQueries(matrixQueries.lists());
      }
    },
  });
}
